/**
 * LiveStreamingRole enum
 *
 * The Agora client role used for a community live-streaming session. This string
 * is the wire `role` payload sent to the `generateLiveStreamToken` Cloud Function
 * (see `cloudFunctionsService.generateLiveStreamToken`), so the value must match
 * Flutter exactly.
 *
 * Flutter passes `_isBroadcaster ? ClientRoleType.clientRoleBroadcaster.name :
 * ClientRoleType.clientRoleAudience.name` as the role
 * (lib/presentation/screens/community/live_streaming_screen.dart:581-583). In
 * Dart, `Enum.name` returns the declared identifier verbatim, so the wire values
 * are the strings `"clientRoleBroadcaster"` / `"clientRoleAudience"`
 * (agora_rtc_engine lib/src/agora_base.dart
 * `enum ClientRoleType { clientRoleBroadcaster, clientRoleAudience, ... }`).
 *
 * NOTE (mirroring `videoCallRole.ts:24-32`): this string is the *token* `role`
 * payload, NOT the local Agora client role. The CF maps it via
 * `data.role.toUpperCase() === "BROADCASTER"`
 * (cloud_functions/functions/src/functions/live_streaming/live_streaming.ts:22);
 * neither `"clientRoleBroadcaster"` nor `"clientRoleAudience"` uppercases to the
 * literal `"BROADCASTER"`, so the issued token is `RtcRole.SUBSCRIBER` for BOTH
 * roles. The broadcaster-vs-audience distinction is enforced by the LOCAL
 * `setClientRole` call (see Task 9), NOT the token role. This mirrors Flutter
 * exactly — do not change the values.
 *
 * `broadcaster` is byte-identical to `VideoCallRole.broadcaster`; the token use
 * case is reused across both features via this string.
 */
export enum LiveStreamingRole {
  /**
   * Broadcaster — the creator publishing the live stream.
   * Wire value matches `ClientRoleType.clientRoleBroadcaster.name` in Flutter.
   */
  broadcaster = 'clientRoleBroadcaster',

  /**
   * Audience — a subscriber joining the live stream view-only.
   * Wire value matches `ClientRoleType.clientRoleAudience.name` in Flutter.
   */
  audience = 'clientRoleAudience',
}
